/* eslint react/destructuring-assignment: 0 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import { HotKeys } from 'react-hotkeys';
import { matchPath } from 'react-router-dom';
import { withLastLocation } from 'react-router-last-location';
import { isDirty, getFormValues } from 'redux-form';
import Preloader from '@omilia/preloader';

import _find from 'lodash/find';
import _isEmpty from 'lodash/isEmpty';

import {
  dictionaryType,
  sortOptionType,
  filterOptionsType,
  paginationOptionsType,
  dictionaryColumnType,
  dictionariesEntry,
  projectType,
} from 'types';

import COMBOS from 'constants/keyboardShortcutsCombos';
import FORM_NAMES from 'constants/formNames';

import * as actions from 'store/services/dictionaries/actions';
import * as selectors from 'store/services/dictionaries/selectors';
import * as entriesSelectors from 'store/services/dictionaryEntries/selectors';
import initialState from 'store/services/dictionaryEntries/initialState';

import * as entriesActions from 'store/services/dictionaryEntries/actions';
import * as columnsActions from 'store/services/dictionaryColumns/actions';
import * as columnsSelectors from 'store/services/dictionaryColumns/selectors';
import {
  getProject,
  getProjectIsLoading,
} from 'store/services/project/selectors';

import SortingMenu from 'components/SortingMenu';
import FilterMenu from 'components/FilterMenu';
import Splitter from 'components/Splitter';
import UnitsSearchResult from 'components/Units/UnitsSearchResult';
import NoUnits from 'components/Units/NoUnits';
import Pagination from 'components/Pagination';
import ListHeaderOptions from 'components/ListHeaderOptions';
import UnsavedChangesModal from 'components/UnsavedChangesModal';

import TestSets from 'scenes/TestSets';

import Inspect from './components/Inspect';
import DictionariesList from './components/DictionariesList';
import CreateNewDictionaryModal from './components/CreateNewDictionaryModal';

import './style.scss';

const HOTKEYS_MAP = {
  create: COMBOS.createNew,
};

export class Dictionaries extends Component {
  handleHotKeys = {
    create: this.handleOpenCreateModal,
  };

  static propTypes = {
    checkDictionaryName: PropTypes.func.isRequired,
    getItem: PropTypes.func.isRequired,
    getItemEntries: PropTypes.func.isRequired,
    getItems: PropTypes.func.isRequired,
    // FILTER:
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    // SORT:
    inspectingDirty: PropTypes.bool.isRequired,
    itemEntriesCount: PropTypes.number.isRequired,
    // PAGINATION:
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
    }).isRequired,
    match: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }).isRequired,
    project: projectType.isRequired,
    // INSPECT:
    projectIsLoading: PropTypes.bool.isRequired,
    onItemAdd: PropTypes.func.isRequired,
    onItemColumnAdd: PropTypes.func.isRequired,
    onItemColumnDelete: PropTypes.func.isRequired,
    // COLLECTIONS:
    onItemColumnUpdate: PropTypes.func.isRequired,

    onItemDelete: PropTypes.func.isRequired,
    onItemDeselect: PropTypes.func.isRequired,
    onItemEntriesDelete: PropTypes.func.isRequired,
    onItemEntriesSort: PropTypes.func.isRequired,
    // ACTIONS:
    onItemEntryAdd: PropTypes.func.isRequired,
    onItemEntryDelete: PropTypes.func.isRequired,
    onItemEntryUpdate: PropTypes.func.isRequired,
    onItemExport: PropTypes.func.isRequired,

    // Project related
    onItemSelect: PropTypes.func.isRequired,
    onItemUpdate: PropTypes.func.isRequired,

    // Last location from withLastLocation
    count: PropTypes.number,

    // TODO: Re-think react-router and select/deselect usage
    dictionaries: PropTypes.arrayOf(dictionaryType),
    filterOptions: PropTypes.arrayOf(filterOptionsType),

    inspectingItem: dictionaryType,
    inspectingItemIndex: PropTypes.number,
    inspectingValues: PropTypes.shape({
      description: PropTypes.string,
      name: PropTypes.string,
    }),

    isExporting: PropTypes.bool,
    isLoading: PropTypes.bool,

    isLoadingItem: PropTypes.bool,
    isLoadingItemEntries: PropTypes.bool,
    itemColumns: PropTypes.arrayOf(dictionaryColumnType),

    itemEntries: PropTypes.arrayOf(dictionariesEntry),
    itemEntriesPagination: paginationOptionsType,
    itemEntriesSelected: PropTypes.arrayOf(PropTypes.string),
    lastLocation: PropTypes.shape({ pathname: PropTypes.string.isRequired }),

    paginationOptions: paginationOptionsType,
    sortOptions: PropTypes.arrayOf(sortOptionType),

    // REACT-ROUTER:
    sortSelectedOption: sortOptionType,

    toggleAllEntriesSelection: PropTypes.func,

    toggleEntrySelection: PropTypes.func,
  };

  state = {
    isCreateModalOpen: false,
    unsavedChangesModal: {
      isOpen: false,
      rejectClb: null,
      confirmClb: null,
    },
  };

  componentDidMount() {
    this.fetchAllItems();
  }

  componentWillReceiveProps({ inspectingItem }) {
    if (!_isEmpty(this.props.inspectingItem) && _isEmpty(inspectingItem)) {
      this.props.history.push(this.props.match.url);
    }
  }

  // MODALS:

  handleOpenCreateModal = () => {
    this.setState({
      isCreateModalOpen: true,
    });
  };

  handleCloseCreateModal = () => {
    this.setState({
      isCreateModalOpen: false,
    });
  };

  handleCreate = async ({ dictionaryName }) => {
    const id = await this.props.onItemAdd({
      name: dictionaryName,
    });

    this.handleListItemSelect({ id });
    this.handleCloseCreateModal();
  };

  handleItemExport = item => {
    if (!this.props.isExporting) {
      this.props.onItemExport(item);
    }
  };

  handleItemDelete = () => {
    this.props.onItemDelete(this.props.inspectingItem.id);
    this.props.onItemDeselect();
  };

  handleCloseUploadModal = shouldReFetchData => {
    if (shouldReFetchData) {
      const { inspectingItem } = this.props;

      // Re-fetches dictionary data after new CSV is uploaded
      if (inspectingItem && inspectingItem.id) {
        this.props.getItemEntries(inspectingItem.id);
      }
    }
  };

  handleOpenUnsavedChangesModal = (confirmClb, rejectClb) => {
    this.setState({
      unsavedChangesModal: {
        isOpen: true,
        rejectClb,
        confirmClb,
      },
    });
  };

  handleCloseUnsavedChangesModal = () => {
    this.setState({
      unsavedChangesModal: {
        isOpen: false,
        rejectClb: null,
        confirmClb: null,
      },
    });
  };

  fetchAllItems = () => {
    this.props.getItems({
      page: 1,
      name__icontains: '',
    });
  };

  searchEntries = (searchTerm = '') => {
    const { inspectingItem } = this.props;
    const paginationProps =
      searchTerm !== ''
        ? { page: undefined, size: undefined }
        : { ...initialState.list.paginationOptions };

    this.props.getItemEntries(inspectingItem.id, {
      ...paginationProps,
      search: searchTerm,
    });
  };

  handleFilter = params => {
    this.props.getItems({ page: 1 }, params);
  };

  handleItemEntryAdd = data => {
    this.props.onItemEntryAdd(this.props.inspectingItem.id, data);
  };

  handleItemEntryDelete = async id => {
    await this.props.onItemEntryDelete(id);
    await this.props.getItems();
  };

  handleItemEntrySort = (id, desc) => {
    const isToken = id === 'token';

    this.props.onItemEntriesSort(this.props.inspectingItem.id, {
      // fixme: Use camelCase
      ordering_column_id: !isToken ? id : undefined,
      ordering: (desc ? '-' : '') + (isToken ? 'token' : 'column'),
    });
  };

  handleItemEntriesChangePage = () => {
    const { size, page } = this.props.itemEntriesPagination;
    const pages = Math.ceil(this.props.itemEntriesCount / size);

    if (page < pages) {
      this.props.getItemEntries(this.props.inspectingItem.id, {
        page: page + 1,
      });
    }
  };

  handleListItemSelect = ({ id }) => {
    const pendingAction = () =>
      this.props.history.push(`${this.props.match.url}/${id}`);

    if (this.props.inspectingDirty) {
      this.handleOpenUnsavedChangesModal(
        async () => {
          await this.props.onItemUpdate(this.props.inspectingValues);

          pendingAction();
        },
        () => {
          // FIXME: Seems UnsavedChangesModal should close itself for `Do Not Save` action
          // and should behave same as `Save`
          this.handleCloseUnsavedChangesModal();

          pendingAction();
        },
      );

      return;
    }

    pendingAction();
  };

  handleItemSelect = id => {
    // FIXME: use id only
    const item = _find(this.props.dictionaries, { id });

    this.props.onItemSelect(item);

    this.props.getItem(id);
    this.props.getItemEntries(id);
  };

  handleInspectClose = () => {
    if (this.props.inspectingDirty) {
      const pendingAction = async () => {
        await this.props.onItemUpdate(this.props.inspectingValues);
      };

      this.handleOpenUnsavedChangesModal(pendingAction, () => {
        this.handleCloseUnsavedChangesModal();
        this.props.onItemDeselect();
      });
    } else {
      this.props.onItemDeselect();
    }
  };

  renderModals = () => {
    const { isCreateModalOpen, unsavedChangesModal } = this.state;

    return (
      <React.Fragment>
        {isCreateModalOpen && (
          <CreateNewDictionaryModal
            onCheckDictionaryName={this.props.checkDictionaryName}
            onClose={this.handleCloseCreateModal}
            onSubmit={this.handleCreate}
          />
        )}
        {unsavedChangesModal.isOpen && (
          <UnsavedChangesModal
            isOpen
            onCancel={this.handleCloseUnsavedChangesModal}
            onDiscard={unsavedChangesModal.rejectClb}
          />
        )}
      </React.Fragment>
    );
  };

  renderList = () => {
    const {
      isLoading,
      projectIsLoading,
      project,
      paginationOptions,
      dictionaries,
      inspectingItem,
      inspectingItemIndex,
    } = this.props;

    const dictionariesLength = dictionaries.length;
    const searchTerm = paginationOptions.name__icontains;

    if (isLoading || projectIsLoading || _isEmpty(project)) {
      return <Preloader />;
    }

    if (dictionariesLength > 0) {
      return (
        <DictionariesList
          currentDictionaryIndex={inspectingItemIndex}
          deselectDictionary={this.handleInspectClose}
          dictionaries={dictionaries}
          selectDictionary={this.handleListItemSelect}
          selectedDictionary={inspectingItem}
        />
      );
    }

    if (searchTerm && searchTerm !== '') {
      return (
        <UnitsSearchResult
          items={dictionaries}
          searchTerm={searchTerm}
          showAll={this.fetchAllItems}
          unitName="dictionary"
        />
      );
    }

    return (
      <NoUnits openModal={this.handleOpenCreateModal} unitName="dictionaries" />
    );
  };

  renderInspect = () => {
    const {
      location,

      isExporting,
      isLoadingItem,
      isLoadingItemEntries,

      itemEntries,
      itemEntriesPagination,
      itemColumns,
      itemEntriesSelected,
      inspectingItem,

      onItemEntryUpdate,
      onItemEntriesDelete,

      onItemColumnAdd,
      onItemColumnUpdate,
      onItemColumnDelete,

      dictionaries,
      toggleEntrySelection,
      toggleAllEntriesSelection,
      onItemDeselect,
    } = this.props;

    const match = matchPath(location.pathname, {
      exact: true,
      path: '/:company/:project/dictionaries/:dictionary',
    });

    if (match && dictionaries.length > 0) {
      return (
        <Inspect
          columns={itemColumns}
          currentDictionary={inspectingItem}
          // REFACTORED PROPS:
          entries={itemEntries}
          entriesPagination={itemEntriesPagination}
          id={match.params.dictionary}
          isExporting={isExporting}
          isLoading={isLoadingItem}
          isLoadingEntries={isLoadingItemEntries}
          searchEntries={this.searchEntries}
          selectedEntries={itemEntriesSelected}
          toggleAllEntriesSelection={toggleAllEntriesSelection}
          toggleEntrySelection={toggleEntrySelection}
          onClose={this.handleInspectClose}
          onCloseUploadModal={this.handleCloseUploadModal}
          onColumnAdd={onItemColumnAdd}
          onColumnDelete={onItemColumnDelete}
          onColumnUpdate={onItemColumnUpdate}
          onDelete={this.handleItemDelete}
          onEntriesChangePage={this.handleItemEntriesChangePage}
          onEntriesDeleteSelected={onItemEntriesDelete}
          onEntryAdd={this.handleItemEntryAdd}
          onEntryDelete={this.handleItemEntryDelete}
          onEntrySort={this.handleItemEntrySort}
          onEntryUpdate={onItemEntryUpdate}
          onExport={this.handleItemExport}
          onItemDeselect={onItemDeselect}
          onSelect={this.handleItemSelect}
        />
      );
    }

    return null;
  };

  // ROOT:

  render() {
    const {
      getItems,
      dictionaries,
      count,
      paginationOptions,
      filterOptions,
      sortOptions,
      sortSelectedOption,
    } = this.props;

    const dictionariesLength = dictionaries.length;

    return (
      <Splitter split="vertical">
        <TestSets />

        <HotKeys
          handlers={this.handleHotKeys}
          keyMap={HOTKEYS_MAP}
          styleName="root"
        >
          {this.renderModals()}

          <ListHeaderOptions
            bottomRightOptions={
              <React.Fragment>
                <FilterMenu
                  filterOptions={filterOptions}
                  modelName="dictionary"
                  onFilter={this.handleFilter}
                />

                <SortingMenu
                  getItems={getItems}
                  items={dictionaries}
                  options={sortOptions}
                  selectedSortOption={sortSelectedOption}
                />
              </React.Fragment>
            }
            getItems={getItems}
            items={dictionaries}
            modelName="dictionary"
            openNewModal={() => this.handleOpenCreateModal()}
            searchTerm={paginationOptions.name__icontains}
          />

          <div className="tabs-content right-tabs height-184">
            <Splitter split="horizontal">
              {this.renderList()}
              {this.renderInspect()}
            </Splitter>
          </div>

          {dictionariesLength > 0 && (
            <Pagination
              className="footer__pagination"
              count={count}
              getItems={getItems}
              paginationOptions={paginationOptions}
            />
          )}
        </HotKeys>
      </Splitter>
    );
  }
}

const mapStateToProps = state => ({
  isLoading: selectors.getIsLoadingList(state),
  isLoadingItem: selectors.getIsShowing(state),
  isLoadingItemEntries: entriesSelectors.getIsLoadingList(state),
  isExporting: selectors.getIsExporting(state),
  // FILTER:
  filterOptions: selectors.getFilterOptions(state),
  // SORT:
  sortOptions: selectors.getFilteredSortOptions(state),
  sortSelectedOption: selectors.getSelectedSortOption(state),
  // PAGINATION:
  count: selectors.getDictionariesListCount(state),
  paginationOptions: selectors.getPaginationOptions(state),
  // INSPECT
  inspectingItem: selectors.getDictionary(state),
  inspectingItemIndex: selectors.getDictionaryIndex(state),
  inspectingDirty: isDirty(FORM_NAMES.DICTIONARIES.INSPECT)(state),
  inspectingValues: getFormValues(FORM_NAMES.DICTIONARIES.INSPECT)(state),
  // COLLECTIONS:
  dictionaries: selectors.getDictionariesList(state),

  itemColumns: columnsSelectors.getDictionariesColumns(state),

  itemEntries: entriesSelectors.getDictionaryEntriesList(state),
  itemEntriesSelected: entriesSelectors.getSelectedEntries(state),
  itemEntriesCount: entriesSelectors.getDictionaryEntriesListCount(state),
  itemEntriesPagination: entriesSelectors.getPaginationOptions(state),

  // Project Related
  project: getProject(state),
  projectIsLoading: getProjectIsLoading(state),
});

const mapDispatchToProps = dispatch => ({
  onItemAdd: bindActionCreators(actions.addDictionary, dispatch),
  onItemUpdate: bindActionCreators(actions.updateDictionary, dispatch),
  onItemExport: bindActionCreators(actions.exportDictionary, dispatch),
  onItemDelete: bindActionCreators(actions.deleteDictionary, dispatch),
  onItemSelect: bindActionCreators(actions.selectDictionary, dispatch),
  onItemDeselect: bindActionCreators(actions.deselectDictionary, dispatch),
  onItemEntryAdd: bindActionCreators(
    entriesActions.addDictionaryEntry,
    dispatch,
  ),
  onItemEntryUpdate: bindActionCreators(
    entriesActions.updateDictionaryEntry,
    dispatch,
  ),
  onItemEntryDelete: bindActionCreators(
    entriesActions.deleteDictionaryEntry,
    dispatch,
  ),
  onItemEntriesDelete: bindActionCreators(
    entriesActions.deleteDictionaryEntries,
    dispatch,
  ),
  onItemEntriesSort: bindActionCreators(
    entriesActions.sortDictionaryEntriesList,
    dispatch,
  ),
  onItemColumnUpdate: bindActionCreators(
    columnsActions.updateDictionaryColumn,
    dispatch,
  ),
  onItemColumnDelete: bindActionCreators(
    columnsActions.deleteDictionaryColumn,
    dispatch,
  ),
  onItemColumnAdd: bindActionCreators(
    columnsActions.addDictionaryColumn,
    dispatch,
  ),

  getItems: bindActionCreators(actions.getDictionariesList, dispatch),
  getItem: bindActionCreators(actions.getDictionary, dispatch),
  checkDictionaryName: bindActionCreators(
    actions.checkDictionaryName,
    dispatch,
  ),
  getItemEntries: bindActionCreators(
    entriesActions.getDictionaryEntriesList,
    dispatch,
  ),

  toggleEntrySelection: bindActionCreators(
    entriesActions.toggleEntrySelection,
    dispatch,
  ),
  toggleAllEntriesSelection: bindActionCreators(
    entriesActions.toggleAllEntriesSelection,
    dispatch,
  ),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withLastLocation,
)(Dictionaries);
